import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../components/Icons'
import { BASE_URL } from '../data/baseurl'

const BRANDS = [
  'DIRA HOLIDAYS Journeys',
  'DIRA HOLIDAYS Retreats',
  'DIRA HOLIDAYS Adventures',
  'DIRA HOLIDAYS Personal Journeys',
  'DIRA HOLIDAYS Escapes',
  'DIRA HOLIDAYS Getaways',
  'DIRA HOLIDAYS Nomad',
  'DIRA HOLIDAYS Experiences',
  'DIRA HOLIDAYS Wanderlust',
  'DIRA HOLIDAYS Explorer',
  'Corporate Business Tours',
]

export default function AddPackage({ embedded = false, onSuccess, onCancel, editPackage = null }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [error, setError] = useState('')
  const [errors, setErrors] = useState({})

  // Package form states
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [brand, setBrand] = useState('DIRA HOLIDAYS Journeys')
  const [duration, setDuration] = useState('4 Days / 3 Nights')
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('India')
  const [region, setRegion] = useState('India')
  const [tag, setTag] = useState('')
  const [minBooking, setMinBooking] = useState('2 Pax')
  const [image, setImage] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [galleryImages, setGalleryImages] = useState(['', '', ''])
  const [galleryFiles, setGalleryFiles] = useState([null, null, null])

  // Dynamic lists states
  const [itinerary, setItinerary] = useState([])
  const [inclusions, setInclusions] = useState([])
  const [exclusions, setExclusions] = useState([])
  const [policies, setPolicies] = useState([])

  // Inputs for additions
  const [newInclusion, setNewInclusion] = useState('')
  const [newExclusion, setNewExclusion] = useState('')
  const [newPolicy, setNewPolicy] = useState('')

  // Upload file via backend (uses uploadService → Cloudinary)
  const uploadToCloudinaryDirect = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${BASE_URL}/upload`, {
      method: 'POST',
      body: formData,
    });

    let json;
    try {
      json = await res.json();
    } catch (e) {
      throw new Error('Backend server is offline or unreachable.');
    }

    if (!res.ok || !json.success) throw new Error(json.error || 'Image upload failed');
    return json.data.url;
  };

  // Handle File selection: show instant preview, store file for later upload
  const handleFileSelect = (e, setPreviewUrl, setFileObj, errorKey) => {
    const file = e.target.files[0];
    if (!file) return;

    const tempErrors = { ...errors };
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      tempErrors[errorKey] = 'Invalid image format. Please select PNG, JPG, JPEG, or WEBP.';
      setErrors(tempErrors);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      tempErrors[errorKey] = 'Image size must be less than 5MB.';
      setErrors(tempErrors);
      return;
    }

    if (tempErrors[errorKey]) {
      delete tempErrors[errorKey];
    }
    setErrors(tempErrors);

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    if (setFileObj) setFileObj(file);
  };

  const updateGalleryImage = (index, url, file) => {
    const updatedImages = [...galleryImages]
    updatedImages[index] = url
    setGalleryImages(updatedImages)

    const updatedFiles = [...galleryFiles]
    updatedFiles[index] = file
    setGalleryFiles(updatedFiles)

    const tempErrors = { ...errors };
    if (tempErrors[`gallery_${index}`]) {
      delete tempErrors[`gallery_${index}`];
      setErrors(tempErrors);
    }
  }

  // Itinerary handlers
  const addItineraryDay = () => {
    setItinerary([...itinerary, { day: itinerary.length + 1, title: '', points: [''] }])
    if (errors.itinerary) setErrors(prev => ({ ...prev, itinerary: '' }))
  }

  const removeItineraryDay = (index) => {
    const updated = itinerary
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, day: i + 1 }))
    setItinerary(updated)

    // Clear specific errors for removed day
    const tempErrors = { ...errors };
    delete tempErrors[`itinerary_title_${index}`];
    delete tempErrors[`itinerary_points_${index}`];
    Object.keys(tempErrors).forEach(key => {
      if (key.startsWith(`itinerary_point_${index}_`)) {
        delete tempErrors[key];
      }
    });
    setErrors(tempErrors);
  }

  const updateItineraryField = (index, field, value) => {
    const updated = itinerary.map((item, i) => {
      if (i !== index) return item
      return { ...item, [field]: value }
    })
    setItinerary(updated)
  }

  const addItineraryPoint = (dayIndex) => {
    const updated = itinerary.map((item, i) => {
      if (i !== dayIndex) return item
      return { ...item, points: [...(item.points || []), ''] }
    })
    setItinerary(updated)
  }

  const updateItineraryPoint = (dayIndex, pointIndex, value) => {
    const updated = itinerary.map((item, i) => {
      if (i !== dayIndex) return item
      const newPoints = (item.points || []).map((p, pi) => pi === pointIndex ? value : p)
      return { ...item, points: newPoints }
    })
    setItinerary(updated)
  }

  const removeItineraryPoint = (dayIndex, pointIndex) => {
    const updated = itinerary.map((item, i) => {
      if (i !== dayIndex) return item
      const newPoints = (item.points || []).filter((_, pi) => pi !== pointIndex)
      return { ...item, points: newPoints.length ? newPoints : [''] }
    })
    setItinerary(updated)

    const tempErrors = { ...errors };
    delete tempErrors[`itinerary_point_${dayIndex}_${pointIndex}`];
    setErrors(tempErrors);
  }

  // Inclusion handlers
  const addInclusion = () => {
    if (newInclusion.trim()) {
      setInclusions([...inclusions, newInclusion.trim()])
      setNewInclusion('')
      if (errors.inclusions) setErrors(prev => ({ ...prev, inclusions: '' }));
    }
  }
  const removeInclusion = (index) => {
    setInclusions(inclusions.filter((_, i) => i !== index))
  }

  // Exclusion handlers
  const addExclusion = () => {
    if (newExclusion.trim()) {
      setExclusions([...exclusions, newExclusion.trim()])
      setNewExclusion('')
      if (errors.exclusions) setErrors(prev => ({ ...prev, exclusions: '' }));
    }
  }
  const removeExclusion = (index) => {
    setExclusions(exclusions.filter((_, i) => i !== index))
  }

  // Policy handlers
  const addPolicy = () => {
    if (newPolicy.trim()) {
      setPolicies([...policies, newPolicy.trim()])
      setNewPolicy('')
      if (errors.policies) setErrors(prev => ({ ...prev, policies: '' }));
    }
  }
  const removePolicy = (index) => {
    setPolicies(policies.filter((_, i) => i !== index))
  }

  // Effect to load data if editing
  useEffect(() => {
    if (editPackage) {
      setName(editPackage.name || '')
      setCode(editPackage.code || '')
      setBrand(editPackage.brand || 'DIRA HOLIDAYS Journeys')
      setDuration(editPackage.duration || '4 Days / 3 Nights')
      setPrice(editPackage.from || String(editPackage.price) || '')
      setCategory(editPackage.cat || 'India')
      setRegion(editPackage.region || 'India')
      setTag(editPackage.tag || '')
      setMinBooking(editPackage.minBooking || '2 Pax')
      setImage(editPackage.image || '')

      const loadedGallery = ['', '', '']
      if (editPackage.galleryImages && Array.isArray(editPackage.galleryImages)) {
        editPackage.galleryImages.forEach((img, idx) => {
          if (idx < 3) loadedGallery[idx] = img
        })
      }
      setGalleryImages(loadedGallery)

      if (editPackage.itinerary && editPackage.itinerary.length > 0) {
        setItinerary(editPackage.itinerary.map((item, i) => ({
          ...item,
          day: item.day || i + 1,
          points: item.points || (item.content ? [item.content] : [''])
        })))
      }
      setInclusions(editPackage.inclusions || [])
      setExclusions(editPackage.exclusions || [])
      setPolicies(editPackage.policies || [])
    }
  }, [editPackage])

  const validateForm = () => {
    const tempErrors = {};
    
    // Basic Details
    if (!name.trim()) tempErrors.name = 'Package name is required.';
    if (!code.trim()) tempErrors.code = 'Package code is required.';
    if (!duration.trim()) tempErrors.duration = 'Duration is required.';
    if (!price.trim()) tempErrors.price = 'Price display text is required.';
    
    // Main Cover Image
    if (!image && !imageFile) {
      tempErrors.image = 'Main cover image is required.';
    } else if (imageFile && imageFile.size > 5 * 1024 * 1024) {
      tempErrors.image = 'Cover image file size must be less than 5MB.';
    } else if (imageFile && !['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(imageFile.type)) {
      tempErrors.image = 'Cover image must be a PNG, JPG, JPEG, or WEBP file.';
    }

    // Gallery Images
    galleryFiles.forEach((file, index) => {
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          tempErrors[`gallery_${index}`] = `Gallery image ${index + 1} size must be less than 5MB.`;
        } else if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
          tempErrors[`gallery_${index}`] = `Gallery image ${index + 1} must be a PNG, JPG, JPEG, or WEBP file.`;
        }
      }
    });

    // Itinerary
    if (itinerary.length === 0) {
      tempErrors.itinerary = 'At least one day of itinerary is required.';
    } else {
      itinerary.forEach((item, index) => {
        if (!item.title.trim()) {
          tempErrors[`itinerary_title_${index}`] = `Title for Day ${item.day} is required.`;
        }
        if (!item.points || item.points.length === 0 || item.points.every(p => !p.trim())) {
          tempErrors[`itinerary_points_${index}`] = `At least one activity point is required for Day ${item.day}.`;
        } else {
          item.points.forEach((point, pIndex) => {
            if (!point.trim()) {
              tempErrors[`itinerary_point_${index}_${pIndex}`] = `Activity point ${pIndex + 1} for Day ${item.day} cannot be empty.`;
            }
          });
        }
      });
    }

    // Inclusions, Exclusions, Policies
    if (inclusions.length === 0) {
      tempErrors.inclusions = 'At least one inclusion is required.';
    }
    if (exclusions.length === 0) {
      tempErrors.exclusions = 'At least one exclusion is required.';
    }
    if (policies.length === 0) {
      tempErrors.policies = 'At least one policy/guideline is required.';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Submit Package Form
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const isValid = validateForm();
    if (!isValid) {
      setError('Form has validation errors. Please check the highlighted fields below.');
      return;
    }

    setLoading(true)
    setError('')

    try {
      // 1. Upload Images directly to Cloudinary from browser
      setUploadingImage(true)

      let finalImageUrl = image // fallback if already a URL
      if (imageFile) {
        finalImageUrl = await uploadToCloudinaryDirect(imageFile)
      }

      let finalGalleryUrls = [...galleryImages]
      for (let i = 0; i < galleryFiles.length; i++) {
        if (galleryFiles[i]) {
          finalGalleryUrls[i] = await uploadToCloudinaryDirect(galleryFiles[i])
        }
      }

      setUploadingImage(false)

      // 2. Submit Package Data
      const newPkg = {
        name,
        code,
        brand,
        duration,
        nights:
          parseInt(duration.match(/(\d+)\s*Night/i)?.[1]) ||
          parseInt(duration.match(/(\d+)\s*N/i)?.[1]) ||
          3,
        price: parseInt(String(price).replace(/\D/g, '')),
        from: price,
        cat: category,
        region,
        tag,
        image: finalImageUrl,
        galleryImages: finalGalleryUrls.filter(
          (url) => url && url.trim() !== '' && !url.startsWith('blob:')
        ),
        minBooking,
        itinerary: itinerary.map((item) => ({
          day: item.day,
          title: item.title,
          content: (item.points || []).filter(p => p.trim() !== '').join('\n'),
          points: (item.points || []).filter(p => p.trim() !== '')
        })),
        inclusions,
        exclusions,
        policies,
      }

      const isEditing = !!editPackage
      const url = isEditing ? `${BASE_URL}/packages/${editPackage._id}` : `${BASE_URL}/packages`

      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPkg),
      })

      if (!res.ok) {
        let errData;
        try {
          errData = await res.json();
        } catch(e) {
          throw new Error('Backend server is down or unreachable (Check your MongoDB IP Whitelist!).');
        }
        throw new Error(errData.message || 'Failed to create package');
      }

      // Redirect on success
      if (onSuccess) onSuccess()
      else navigate('/holidays')
    } catch (err) {
      setError(err.message || 'Failed to connect to backend server.')
      console.error(err)
    } finally {
      setLoading(false)
      setUploadingImage(false)
    }
  }

  return (
    <div
      className={embedded ? '' : 'page-enter'}
      style={embedded ? {} : { background: '#f8fafc', padding: '60px 20px' }}
    >
      <div
        style={{
          maxWidth: '880px',
          margin: '0 auto',
          background: '#fff',
          borderRadius: '16px',
          border: '1.5px solid #e2e8f0',
          boxShadow: embedded ? 'none' : '0 10px 30px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #0d1117 0%, #0202bf 100%)',
            padding: '36px 30px',
            color: '#fff',
            position: 'relative',
          }}
        >
          <h1 style={{ fontSize: '26px', fontWeight: 800, margin: '0 0 8px' }}>
            {editPackage ? 'Edit Package' : 'Create New Package'}
          </h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
            Add a new holiday tour to the database. All edits are saved dynamically to the backend
            database.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '30px' }}>
          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                color: '#b91c1c',
                padding: '14px 18px',
                borderRadius: '10px',
                marginBottom: '28px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <span style={{ flex: 1, fontWeight: 700 }}>{error}</span>
              <button
                onClick={() => setError('')}
                type="button"
                style={{
                  background: '#fee2e2',
                  border: 'none',
                  color: '#b91c1c',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* SECTION 1: BASIC INFORMATION */}
          <h3
            style={{
              fontSize: '15px',
              color: 'var(--brand)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 800,
              borderBottom: '1.5px solid #e2e8f0',
              paddingBottom: '8px',
              marginBottom: '20px',
            }}
          >
            Basic Details
          </h3>

          <div
            className="add-pkg-form-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '24px',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Package Name *
              </label>
              <input
                type="text"
                placeholder="e.g. Divine Tirupati & Vibrant Chennai"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${errors.name ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              {errors.name && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {errors.name}
                </span>
              )}
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Package Code *
              </label>
              <input
                type="text"
                placeholder="e.g. DHIB001"
                required
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (errors.code) setErrors(prev => ({ ...prev, code: '' }));
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${errors.code ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              {errors.code && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {errors.code}
                </span>
              )}
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Brand Universe *
              </label>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13px',
                  background: '#fff',
                }}
              >
                {BRANDS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Category *
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13px',
                  background: '#fff',
                }}
              >
                <option value="India">India</option>
                <option value="International">International</option>
                <option value="Luxury">Luxury</option>
                <option value="Adventure">Adventure</option>
                <option value="Beach">Beach</option>
                <option value="Short Break">Short Break</option>
                <option value="Family">Family</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Destination Region *
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13px',
                  background: '#fff',
                }}
              >
                <option value="India">India</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Singapore">Singapore</option>
                <option value="Japan">Japan</option>
                <option value="Cambodia">Cambodia</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Philippines">Philippines</option>
                <option value="Vietnam">Vietnam</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Duration *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. 4 Days / 3 Nights"
                value={duration}
                onChange={(e) => {
                  setDuration(e.target.value);
                  if (errors.duration) setErrors(prev => ({ ...prev, duration: '' }));
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${errors.duration ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              {errors.duration && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {errors.duration}
                </span>
              )}
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Price Display *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. INR 21999/-PP"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) setErrors(prev => ({ ...prev, price: '' }));
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1.5px solid ${errors.price ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
              {errors.price && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  {errors.price}
                </span>
              )}
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Tag / Highlight
              </label>
              <input
                type="text"
                placeholder="e.g. Bestseller, Divine Darshan"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                }}
              >
                Minimum Booking
              </label>
              <input
                type="text"
                placeholder="e.g. 2 Pax"
                value={minBooking}
                onChange={(e) => setMinBooking(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>

          {/* SECTION 2: CLOUDINARY IMAGE UPLOAD */}
          <h3
            style={{
              fontSize: '15px',
              color: 'var(--brand)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 800,
              borderBottom: '1.5px solid #e2e8f0',
              paddingBottom: '8px',
              marginBottom: '20px',
              marginTop: '36px',
            }}
          >
            Tour Images
          </h3>

          <div
            className="add-pkg-images-row"
            style={{
              background: '#f8fafc',
              padding: '24px',
              borderRadius: '16px',
              border: `1.5px dashed ${errors.image ? '#ef4444' : '#cbd5e1'}`,
              display: 'flex',
              gap: '24px',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#3d4a5c',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                }}
              >
                Main Cover Image (Hero Banner) *
              </label>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  background: '#fff',
                  border: `1.5px dashed ${errors.image ? '#ef4444' : '#94a3b8'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0202bf'
                  e.currentTarget.style.background = '#f0f4ff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = errors.image ? '#ef4444' : '#94a3b8'
                  e.currentTarget.style.background = '#fff'
                }}
              >
                <Icon name="camera" size={28} color="#6b7a8f" />
                <span
                  style={{ fontSize: '13px', fontWeight: 800, color: '#0d1117', marginTop: '10px' }}
                >
                  Click to Choose Cover
                </span>
                <span style={{ fontSize: '11px', color: '#6b7a8f', marginTop: '4px' }}>
                  PNG, JPG up to 5MB
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, setImage, setImageFile, 'image')}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            {image && (
              <div
                style={{
                  width: '180px',
                  height: '120px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid #fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <img
                  src={image}
                  alt="cover preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>
          {errors.image && (
            <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: 600, marginBottom: '24px', marginTop: '-8px', paddingLeft: '8px' }}>
              ⚠️ {errors.image}
            </div>
          )}

          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {[0, 1, 2].map((idx) => (
              <div
                key={idx}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  background: '#f8fafc',
                  padding: '20px',
                  borderRadius: '16px',
                  border: `1.5px dashed ${errors[`gallery_${idx}`] ? '#ef4444' : '#cbd5e1'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <label
                  style={{
                    fontSize: '11px',
                    fontWeight: 800,
                    color: '#3d4a5c',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  }}
                >
                  Gallery Image {idx + 1}
                </label>

                {!galleryImages[idx] ? (
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px',
                      background: '#fff',
                      border: `1.5px dashed ${errors[`gallery_${idx}`] ? '#ef4444' : '#94a3b8'}`,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      flex: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#0202bf'
                      e.currentTarget.style.background = '#f0f4ff'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = errors[`gallery_${idx}`] ? '#ef4444' : '#94a3b8'
                      e.currentTarget.style.background = '#fff'
                    }}
                  >
                    <Icon name="camera" size={20} color="#6b7a8f" />
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#3d4a5c',
                        marginTop: '8px',
                      }}
                    >
                      Choose
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleFileSelect(
                          e,
                          (url) => updateGalleryImage(idx, url, e.target.files[0]),
                          null,
                          `gallery_${idx}`
                        )
                      }
                      style={{ display: 'none' }}
                    />
                  </label>
                ) : (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '2px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    <img
                      src={galleryImages[idx]}
                      alt={`gallery ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <label
                      style={{
                        position: 'absolute',
                        bottom: '6px',
                        right: '6px',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        zIndex: 2,
                      }}
                    >
                      Change
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleFileSelect(
                            e,
                            (url) => updateGalleryImage(idx, url, e.target.files[0]),
                            null,
                            `gallery_${idx}`
                          )
                        }
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                )}
                {errors[`gallery_${idx}`] && (
                  <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px', display: 'block', textAlign: 'center' }}>
                    {errors[`gallery_${idx}`]}
                  </span>
                )}
              </div>
            ))}
          </div>

          {uploadingImage && (
            <div
              style={{
                fontSize: '12px',
                color: 'var(--brand)',
                marginBottom: '16px',
                fontWeight: 700,
              }}
            >
              ⏳ Uploading image to Cloudinary...
            </div>
          )}

          <div
            style={{
              fontSize: '12px',
              color: '#6b7a8f',
              background: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              marginTop: '-12px',
              marginBottom: '24px',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: '14px' }}>ℹ️</span>
            <div>
              <strong>Where do these images appear?</strong>
              <div style={{ marginTop: '4px', lineHeight: 1.5 }}>
                The <strong>Main Cover Image</strong> is used on the Home page cards, Holidays page,
                and as the large Hero Banner in the Package Details page. <br />
                The <strong>Gallery Images</strong> appear as small thumbnails directly underneath
                the main banner on the Package Details page.
              </div>
            </div>
          </div>

          {/* SECTION 3: DAY WISE ITINERARY */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1.5px solid #e2e8f0',
              paddingBottom: '8px',
              marginBottom: '20px',
              marginTop: '36px',
            }}
          >
            <h3
              style={{
                fontSize: '15px',
                color: 'var(--brand)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 800,
                margin: 0,
              }}
            >
              Day-wise Itinerary
            </h3>
            <button
              type="button"
              onClick={addItineraryDay}
              style={{
                background: 'var(--brand)',
                border: 'none',
                color: '#fff',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <Icon name="plus" size={10} color="#fff" /> ADD DAY
            </button>
          </div>

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}
          >
            {errors.itinerary && (
              <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>
                ⚠️ {errors.itinerary}
              </div>
            )}
            {itinerary.map((item, idx) => (
              <div
                key={idx}
                style={{
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '10px',
                  border: `1.5px solid ${errors[`itinerary_title_${idx}`] || errors[`itinerary_points_${idx}`] ? '#ef4444' : '#cbd5e1'}`,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--brand)' }}>
                    Day {item.day}
                  </span>
                  {itinerary.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItineraryDay(idx)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder={`Day ${item.day} Title (e.g. Arrival in Chennai)`}
                    required
                    value={item.title}
                    onChange={(e) => {
                      updateItineraryField(idx, 'title', e.target.value);
                      if (errors[`itinerary_title_${idx}`]) {
                        setErrors(prev => ({ ...prev, [`itinerary_title_${idx}`]: '' }));
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: `1.5px solid ${errors[`itinerary_title_${idx}`] ? '#ef4444' : '#cbd5e1'}`,
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  />
                  {errors[`itinerary_title_${idx}`] && (
                    <span style={{ color: '#ef4444', fontSize: '11px', display: 'block', marginTop: '-4px' }}>
                      {errors[`itinerary_title_${idx}`]}
                    </span>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                    {(item.points || []).map((point, pIdx) => (
                      <div key={pIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#cbd5e1', flexShrink: 0 }} />
                          <input
                            type="text"
                            placeholder={`Activity point ${pIdx + 1}...`}
                            required
                            value={point}
                            onChange={(e) => {
                              updateItineraryPoint(idx, pIdx, e.target.value);
                              if (errors[`itinerary_point_${idx}_${pIdx}`]) {
                                setErrors(prev => {
                                  const updated = { ...prev };
                                  delete updated[`itinerary_point_${idx}_${pIdx}`];
                                  return updated;
                                });
                              }
                              if (errors[`itinerary_points_${idx}`]) {
                                setErrors(prev => {
                                  const updated = { ...prev };
                                  delete updated[`itinerary_points_${idx}`];
                                  return updated;
                                });
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: '8px 10px',
                              border: `1.5px solid ${errors[`itinerary_point_${idx}_${pIdx}`] || errors[`itinerary_points_${idx}`] ? '#ef4444' : '#cbd5e1'}`,
                              borderRadius: '6px',
                              fontSize: '13px',
                            }}
                          />
                          {(item.points || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItineraryPoint(idx, pIdx)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                padding: '4px',
                              }}
                              title="Remove Point"
                            >
                              <Icon name="close" size={16} color="#ef4444" />
                            </button>
                          )}
                        </div>
                        {errors[`itinerary_point_${idx}_${pIdx}`] && (
                          <span style={{ color: '#ef4444', fontSize: '11px', marginLeft: '14px', display: 'block' }}>
                            {errors[`itinerary_point_${idx}_${pIdx}`]}
                          </span>
                        )}
                      </div>
                    ))}
                    {errors[`itinerary_points_${idx}`] && (
                      <span style={{ color: '#ef4444', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                        {errors[`itinerary_points_${idx}`]}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => addItineraryPoint(idx)}
                      style={{
                        alignSelf: 'flex-start',
                        background: 'none',
                        border: 'none',
                        color: 'var(--brand)',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: '4px 0',
                        marginTop: '4px'
                      }}
                    >
                      + Add Activity Point
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* SECTION 4: INCLUSIONS & EXCLUSIONS */}
          <div
            className="add-pkg-inc-exc-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '30px',
              marginTop: '36px',
            }}
          >
            {/* Inclusions */}
            <div>
              <h3
                style={{
                  fontSize: '15px',
                  color: 'var(--brand)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 800,
                  borderBottom: `1.5px solid ${errors.inclusions ? '#ef4444' : '#e2e8f0'}`,
                  paddingBottom: '8px',
                  marginBottom: '16px',
                }}
              >
                Inclusions
              </h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Add inclusion..."
                  value={newInclusion}
                  onChange={(e) => setNewInclusion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInclusion())}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: `1.5px solid ${errors.inclusions ? '#ef4444' : '#cbd5e1'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                  }}
                />
                <button
                  type="button"
                  onClick={addInclusion}
                  style={{
                    padding: '8px 14px',
                    background: '#0202bf',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Add
                </button>
              </div>
              {errors.inclusions && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '-6px', marginBottom: '10px', display: 'block' }}>
                  ⚠️ {errors.inclusions}
                </span>
              )}
              <ul
                style={{
                  paddingLeft: '20px',
                  margin: 0,
                  fontSize: '13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {inclusions.map((inc, i) => (
                  <li key={i} style={{ color: '#3d4a5c' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{inc}</span>
                      <button
                        type="button"
                        onClick={() => removeInclusion(i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          fontSize: '10px',
                          cursor: 'pointer',
                          padding: '0 4px',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Exclusions */}
            <div>
              <h3
                style={{
                  fontSize: '15px',
                  color: 'var(--brand)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 800,
                  borderBottom: `1.5px solid ${errors.exclusions ? '#ef4444' : '#e2e8f0'}`,
                  paddingBottom: '8px',
                  marginBottom: '16px',
                }}
              >
                Exclusions
              </h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Add exclusion..."
                  value={newExclusion}
                  onChange={(e) => setNewExclusion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusion())}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: `1.5px solid ${errors.exclusions ? '#ef4444' : '#cbd5e1'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                  }}
                />
                <button
                  type="button"
                  onClick={addExclusion}
                  style={{
                    padding: '8px 14px',
                    background: '#0202bf',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Add
                </button>
              </div>
              {errors.exclusions && (
                <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '-6px', marginBottom: '10px', display: 'block' }}>
                  ⚠️ {errors.exclusions}
                </span>
              )}
              <ul
                style={{
                  paddingLeft: '20px',
                  margin: 0,
                  fontSize: '13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {exclusions.map((exc, i) => (
                  <li key={i} style={{ color: '#3d4a5c' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{exc}</span>
                      <button
                        type="button"
                        onClick={() => removeExclusion(i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          fontSize: '10px',
                          cursor: 'pointer',
                          padding: '0 4px',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* SECTION 5: POLICIES */}
          <div style={{ marginTop: '36px' }}>
            <h3
              style={{
                fontSize: '15px',
                color: 'var(--brand)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 800,
                borderBottom: `1.5px solid ${errors.policies ? '#ef4444' : '#e2e8f0'}`,
                paddingBottom: '8px',
                marginBottom: '16px',
              }}
            >
              Policies & Guidelines
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Add policy..."
                value={newPolicy}
                onChange={(e) => setNewPolicy(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPolicy())}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  border: `1.5px solid ${errors.policies ? '#ef4444' : '#cbd5e1'}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
              />
              <button
                type="button"
                onClick={addPolicy}
                style={{
                  padding: '8px 14px',
                  background: '#0202bf',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
            {errors.policies && (
              <span style={{ color: '#ef4444', fontSize: '11px', marginTop: '-6px', marginBottom: '10px', display: 'block' }}>
                ⚠️ {errors.policies}
              </span>
            )}
            <ul
              style={{
                paddingLeft: '20px',
                margin: 0,
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {policies.map((pol, i) => (
                <li key={i} style={{ color: '#3d4a5c' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{pol}</span>
                    <button
                      type="button"
                      onClick={() => removePolicy(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        fontSize: '10px',
                        cursor: 'pointer',
                        padding: '0 4px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* SUBMIT BUTTON */}
          <div
            className="add-pkg-submit-row"
            style={{
              marginTop: '40px',
              paddingTop: '20px',
              borderTop: '1.5px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (onCancel) onCancel()
                else navigate('/holidays')
              }}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: '1.5px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 700,
                color: '#3d4a5c',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImage}
              style={{
                padding: '12px 30px',
                background: '#ffb600',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 800,
                color: '#0d1117',
                cursor: loading || uploadingImage ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(255,182,0,0.2)',
                opacity: loading || uploadingImage ? 0.7 : 1,
              }}
            >
              {loading ? 'Processing...' : (editPackage ? 'Update Package' : 'Create Package')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
